import * as React from 'react';
import cx from 'classnames';
import { Tooltip, Dropdown, Menu } from 'antd';
import { HighlighterIcon, ChevronDownIcon } from '@revfluence/fresh-icons/regular/esm';

import { PaintBucketIcon } from 'lucide-react';
import { TTextColor, THighlightColor } from './types';

const DEFAULT_COLORS = [
  '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
  '#FF0000', '#FF8000', '#FFFF00', '#80FF00', '#00FF00', '#00FF80',
  '#00FFFF', '#0080FF', '#0000FF', '#8000FF', '#FF00FF', '#FF0080',
  '#8B4513', '#A0522D', '#CD853F', '#D2691E', '#DEB887', '#F4A460',
];

interface IProps {
  currentTextColor?: TTextColor;
  currentHighlightColor?: THighlightColor;
  onTextColorChange(color: TTextColor): void;
  onHighlightColorChange(color: THighlightColor): void;
  className?: string;
}

export const ColorControls: React.FC<IProps> = React.memo((props) => {
  const { currentTextColor = '#000000', currentHighlightColor = 'transparent' } = props;

  const textColorMenu = (
    <Menu className="p-2 min-w-[200px]">
      <div className="grid grid-cols-6 gap-1 mb-2">
        {DEFAULT_COLORS.map((color) => (
          <div
            key={color}
            className={cx(
              'w-5 h-5 rounded-sm cursor-pointer border-2 border-transparent transition-colors hover:border-blue-400',
              {
                'border-blue-600': color === currentTextColor,
              }
            )}
            style={{ backgroundColor: color }}
            onClick={() => props.onTextColorChange(color)}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
        <input
          type="color"
          value={currentTextColor}
          onChange={(e) => props.onTextColorChange(e.target.value)}
          className="w-7 h-7 border-none rounded cursor-pointer"
        />
        <span className="text-xs text-gray-600">Custom</span>
      </div>
    </Menu>
  );

  const highlightColorMenu = (
    <Menu className="p-2 min-w-[200px]">
      <div className="grid grid-cols-6 gap-1 mb-2">
        <div
          className={cx(
            'w-5 h-5 rounded-sm cursor-pointer border-2 border-transparent transition-colors hover:border-blue-400 flex items-center justify-center',
            'bg-gradient-to-r from-gray-300 to-transparent bg-[length:4px_4px] bg-[position:0_0,0_2px,2px_-2px,-2px_0]',
            {
              'border-blue-600': currentHighlightColor === 'transparent',
            }
          )}
          style={{
            backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
            backgroundSize: '4px 4px',
            backgroundPosition: '0 0, 0 2px, 2px -2px, -2px 0px'
          }}
          onClick={() => props.onHighlightColorChange('transparent')}
        >
          <span className="text-[10px] text-gray-600 font-bold">None</span>
        </div>
        {DEFAULT_COLORS.map((color) => (
          <div
            key={color}
            className={cx(
              'w-5 h-5 rounded-sm cursor-pointer border-2 border-transparent transition-colors hover:border-blue-400',
              {
                'border-blue-600': color === currentHighlightColor,
              }
            )}
            style={{ backgroundColor: color }}
            onClick={() => props.onHighlightColorChange(color)}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
        <input
          type="color"
          value={currentHighlightColor === 'transparent' ? '#FFFF00' : currentHighlightColor}
          onChange={(e) => props.onHighlightColorChange(e.target.value)}
          className="w-7 h-7 border-none rounded cursor-pointer"
        />
        <span className="text-xs text-gray-600">Custom</span>
      </div>
    </Menu>
  );

  return (
    <div className={cx('flex items-center gap-1', props.className)}>
      <Tooltip title="Text Color">
        <Dropdown overlay={textColorMenu} trigger={['click']} placement="bottomLeft">
          <div className="flex items-center p-1.5 border border-gray-300 rounded bg-white cursor-pointer gap-1 transition-all hover:border-blue-400 hover:bg-blue-50">
            <PaintBucketIcon width={14} height={14} />
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: currentTextColor }}
            />
            <ChevronDownIcon width={10} height={10} />
          </div>
        </Dropdown>
      </Tooltip>

      <Tooltip title="Highlight Color">
        <Dropdown overlay={highlightColorMenu} trigger={['click']} placement="bottomLeft">
          <div className="flex items-center p-1.5 border border-gray-300 rounded bg-white cursor-pointer gap-1 transition-all hover:border-blue-400 hover:bg-blue-50">
            <HighlighterIcon width={14} height={14} />
            <div
              className={cx('w-3 h-3 rounded-sm', {
                'border border-gray-400': currentHighlightColor === 'transparent'
              })}
              style={{
                backgroundColor: currentHighlightColor === 'transparent' ? 'transparent' : currentHighlightColor,
              }}
            />
            <ChevronDownIcon width={10} height={10} />
          </div>
        </Dropdown>
      </Tooltip>
    </div>
  );
});

ColorControls.displayName = 'ColorControls';
